import { useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { ColumnDef } from '@tanstack/react-table';

import { Button } from '@/components/ui/button';
import { Table } from '@/components/table';
import { DatasetQuerySuccess } from '@/hooks/use-dataset-query';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { CopyButton } from '@/components/ui/copy-button';

interface DatasetQueryResultsProps {
  result: DatasetQuerySuccess;
  lastSql?: string | null;
}

export const DatasetQueryResults = ({
  result,
  lastSql,
}: DatasetQueryResultsProps) => {
  const rows = useMemo(() => result.rows || [], [result.rows]);
  const columns = useMemo(() => result.columns || [], [result.columns]);
  const [viewMode, setViewMode] = useState<'table' | 'json'>('table');

  const hasTabularData = columns.length > 0;
  const hasResults = hasTabularData || rows.length > 0;

  const tableColumns = useMemo<ColumnDef<Record<string, unknown>>[]>(
    () =>
      columns.map((column) => ({
        accessorKey: column,
        header: column,
        cell: ({ row }) => {
          const value = row.original[column];
          return value ?? '—';
        },
      })),
    [columns],
  );

  const jsonPayload = useMemo(
    () => ({
      columns,
      rows,
    }),
    [columns, rows],
  );

  const jsonString = useMemo(() => {
    try {
      return JSON.stringify(
        jsonPayload,
        (_key, value) => (typeof value === 'bigint' ? value.toString() : value),
        2,
      );
    } catch (error) {
      console.error('Failed to serialise dataset query result to JSON:', error);
      return '[]';
    }
  }, [jsonPayload]);

  const handleDownloadCsv = () => {
    if (!hasTabularData) return;

    const header = columns.map((col) => `"${col.replace(/"/g, '""')}"`);
    const csvRows = rows.map((row) =>
      columns
        .map((column) => {
          const cell = row[column];
          if (cell === null || cell === undefined) return '""';
          return `"${String(cell).replace(/"/g, '""')}"`;
        })
        .join(','),
    );
    const csvContent = [header.join(','), ...csvRows].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `dataset-query-${result.query_execution_id}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadJson = () => {
    const blob = new Blob([jsonString], {
      type: 'application/json;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `dataset-query-${result.query_execution_id}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleViewChange = (value: string) => {
    if (value === 'table' || value === 'json') {
      setViewMode(value);
    }
  };

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='text-sm text-muted-foreground'>
          Returned {rows.length} row{rows.length === 1 ? '' : 's'}
        </div>
        {hasResults && (
          <div className='flex flex-wrap items-center gap-2'>
            <ToggleGroup
              type='single'
              size='sm'
              value={viewMode}
              onValueChange={handleViewChange}
            >
              <ToggleGroupItem value='table'>Table</ToggleGroupItem>
              <ToggleGroupItem value='json'>JSON</ToggleGroupItem>
            </ToggleGroup>
            {hasTabularData && rows.length > 0 && (
              <Button
                type='button'
                size='sm'
                variant='outline'
                className='h-7 text-xs'
                onClick={handleDownloadCsv}
              >
                <Download className='w-3.5 h-3.5 mr-1' /> Download CSV
              </Button>
            )}
            <Button
              type='button'
              size='sm'
              variant='outline'
              className='h-7 text-xs'
              onClick={handleDownloadJson}
            >
              <Download className='w-3.5 h-3.5 mr-1' /> Download JSON
            </Button>
          </div>
        )}
      </div>

      {viewMode === 'table' ? (
        <div className='overflow-auto max-h-[360px]'>
          <Table columns={tableColumns} data={rows} loading={false} />
        </div>
      ) : (
        <pre className='max-h-[360px] overflow-auto rounded-md border bg-muted/50 p-3 text-xs'>
          {jsonString}
        </pre>
      )}

      <div className='space-y-2 text-sm text-muted-foreground'>
        <p>
          <span className='font-medium text-foreground'>
            Query execution ID:
          </span>{' '}
          {result.query_execution_id}
        </p>
        {result.output_location && (
          <p>
            <span className='font-medium text-foreground'>
              Output location:
            </span>{' '}
            {result.output_location}
          </p>
        )}
      </div>

      {(result.sql || lastSql) && (
        <div className='space-y-2'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <p className='font-medium text-sm'>
                {result.strategy === 'best_of_n' ? 'Best SQL' : 'Generated SQL'}
              </p>
              {result.strategy === 'best_of_n' &&
                result.candidates_generated != null &&
                result.candidates_succeeded != null && (
                  <span className='text-xs text-muted-foreground'>
                    ({result.candidates_succeeded}/{result.candidates_generated}{' '}
                    {result.candidates_generated === 1
                      ? 'candidate'
                      : 'candidates'}{' '}
                    succeeded)
                  </span>
                )}
            </div>
            <CopyButton
              text={result.sql || lastSql || ''}
              size='small'
              ariaLabel='Copy SQL'
            />
          </div>
          <pre className='max-h-[240px] overflow-auto rounded-md border bg-muted/50 p-3 text-sm whitespace-pre-wrap'>
            {result.sql || lastSql}
          </pre>
        </div>
      )}
    </div>
  );
};
